import "react-datepicker/dist/react-datepicker.css";
import {
  Badge,
  Button,
  Card,
  Col,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import TablePaginationBar from "../components/TablePaginationBar";
import { getConfigWithToken } from "../config/config";
import { toast, ToastContainer } from "react-toastify";
import { formatCredentials } from "../utils/formatCredentials";
import { useEffect, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import DatePicker from "react-datepicker";
import formatDate from "../utils/formatDate";
import { createPortal } from "react-dom";
import SessionHistoryPDFGenerator from "../outlets/SessionHistoryPDFGenerator";
import FollowUpPDFGenerator from "../outlets/FollowUpPDFGenerator";

import { FaSearch } from "react-icons/fa";
const SessionHistory = () => {
  const [appointmentsPerPage, setAppointmentsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [paginateAppointments, setPaginateAppointments] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [showAllSessions, setShowAllSessions] = useState(false);
  const [totalSessions, setTotalSessions] = useState(0);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showSessionDetail, setShowSessionDetail] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [statusTypeFilter, setStatusTypeFilter] = useState("All");
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownButtonRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSessionForModal, setSelectedSessionForModal] = useState(null);
  const [showNoShowModal, setShowNoShowModal] = useState(false);
  const [selectedSessionForNoShow, setSelectedSessionForNoShow] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedSessionForCancel, setSelectedSessionForCancel] = useState(null);
  const [sessionHistoryDetailsBySession, setSessionHistoryDetailsBySession] = useState({});
  const [loadingSessionDetails, setLoadingSessionDetails] = useState({});
  const [paymentDataBySession, setPaymentDataBySession] = useState({});

  const user = useSelector((state) => state?.user);
  const navigate = useNavigate();
  const location = useLocation();

  const clearSearchFilter = () => {
    setDebouncedSearch("");
    setSearch("");
  };

  const closeSessionDetail = () => {
    setShowSessionDetail(false);
    setSelectedSession(null);
    setSummaryData(null);
  };

  const fetchSessionHistoryDetailsForSessions = async (sessionsList) => {
    try {
      const sessionIds = (sessionsList || [])
        .map((session) => session._id || session.id)
        .filter(Boolean);

      if (sessionIds.length === 0) {
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/sessionHistory/get-by-sessions`,
        { sessionIds },
        getConfigWithToken(user.user?.token)
      );

      if (response.data.success && response.data.data) {
        const detailsMap = {};
        response.data.data.forEach((detail) => {
          if (detail.sessionId) {
            detailsMap[detail.sessionId] = detail;
          }
        });

        setSessionHistoryDetailsBySession((prev) => ({
          ...prev,
          ...detailsMap,
        }));
      }
    } catch (error) {
      console.error("-> -> fetchSessionHistoryDetailsForSessions -> error->", error);
    }
  };

  const fetchSessions = async () => {
    try {
      setSessionsLoading(true);
      const params = {};

      const formatDate = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
      };

      if (selectedStartDate && selectedEndDate) {
        params.startDate = formatDate(selectedStartDate);
        params.endDate = formatDate(selectedEndDate);

      } else if (selectedStartDate && !selectedEndDate) {
        params.date = formatDate(selectedStartDate);
      }

      if (debouncedSearch && debouncedSearch.trim() !== "") {
        params.search = debouncedSearch.trim();
      }

      params.limit = appointmentsPerPage;
      params.skip = (currentPage - 1) * appointmentsPerPage;
      params.lightweight = true;

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/appointment/getSessionHistory`,
        { params, ...getConfigWithToken(user.user?.token) }
      );

      if (response.data.success) {
        const sessionsData = response.data.data ?? [];

        const transformedSessions = sessionsData.map((session) => ({
          ...session,
          _id: session._id ?? session.id ?? Math.random().toString(),
          appointmentdate:
            session.appointmentdate ?? session.date ?? session.appointmenttime,
          appointmenttime:
            session.appointmenttime ?? session.appointmentdate ?? session.date,
          status: session.status ?? "Scheduled",
        }));

        setSessions(transformedSessions);
        const serverTotal =
          response.data.total ?? response.data.totalCount ?? null;
        setTotalSessions(
          Number.isFinite(serverTotal) ? serverTotal : transformedSessions.length
        );

        // Extract payment data from response and set it directly
        const paymentDataMap = {};
        transformedSessions.forEach((session) => {
          const sessionId = session._id || session.id;
          if (sessionId && session.paymentData) {
            paymentDataMap[sessionId] = session.paymentData;
          } else if (sessionId) {
            paymentDataMap[sessionId] = null;
          }
        });
        setPaymentDataBySession((prev) => ({ ...prev, ...paymentDataMap }));
        fetchSessionHistoryDetailsForSessions(transformedSessions);
      } else {
        toast.error("Failed to fetch sessions data.");
      }
    } catch (error) {
      console.error("-> -> fetchSessions -> error->", error);

      toast.error(error?.response?.data?.message);
    } finally {
      setSessionsLoading(false);
    }
  };

  const fetchTranscriptionSummary = async (appointmentId) => {
    try {
      setSummaryLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/transcription/get-ai-summary/${appointmentId}`,
        getConfigWithToken(user.user?.token)
      );

      if (response.data) {
        setShowSessionDetail(true);
        setSummaryData(response.data);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("-> -> fetchTranscriptionSummary -> error->", error);

      toast.error(error.response.data.message);
    } finally {
      setSummaryLoading(false);
    }
  };

  const getActualStatus = (session) => {
    const sessionId = session._id || session.id;
    const sessionDetail = sessionHistoryDetailsBySession[sessionId];
    const now = new Date();

    // Derive start/end times with fallbacks
    const appointmentStartTime = new Date(session.appointmenttime);
    const appointmentEndTime = new Date(session.appointmentEndTime);

    const hasValidStart = !isNaN(appointmentStartTime.getTime());
    const hasValidEnd = !isNaN(appointmentEndTime.getTime());

    const status = session.status;
    const isCancelled = session.isCancelled === true;

    /* =======================
       CANCEL STATUS HANDLING
       ======================= */
    if (
      isCancelled ||
      [
        "Cancelled",
        "Canceled",
        "canceled_by_user",
        "Canceled by Provider",
        "Canceled by Super Admin",
        "System Cancel",
        "late_cancelled",
        "late_cancel"
      ].includes(status)
    ) {
      if (status === "canceled_by_user") {
        return "User Cancel";
      }

      if (status === "Canceled by Provider") {
        return "Provider Cancel";
      }

      if (status === "Canceled by Super Admin" || status === "System Cancel") {
        return "Admin Cancel";
      }

      if (status === "late_cancelled" || status === "late_cancel") {
        return "Late Cancel";
      }

      return "Cancelled";
    }

    /* =======================
       NON-CANCEL STATUSES
       ======================= */
    if (status === "No Show") {
      return "No Show";
    }

    if (status === "Completed") {
      return "Completed";
    }

    // Session started logic
    if (hasValidStart && now >= appointmentStartTime) {
      if (hasValidEnd && now > appointmentEndTime) {
        // After end time → Completed only if signed
        return sessionDetail?.isSigned === true ? "Completed" : "Pending";
      }

      // During session window
      return "Pending";
    }

    // Future session
    return "Scheduled";
  };


  const getFilteredSessions = () => {
    let filtered = sessions;

    // Filter by status
    if (statusFilter !== "All") {
      filtered = filtered.filter((session) => {
        const actualStatus = getActualStatus(session);

        return actualStatus === statusFilter;
      });
    }

    // Filter by appointment type
    if (statusTypeFilter !== "All") {
      filtered = filtered.filter((session) => {
        if (statusTypeFilter === "initial") {
          return session.appointmentType === "initial";
        } else if (statusTypeFilter === "followUp") {
          return session.appointmentType === "Follow-Up";
        }
        return true;
      });
    }

    return filtered;
  };

  const getStatusBadge = (session) => {
    const actualStatus = getActualStatus(session);

    const statusConfig = {
      Completed: { bg: "#D0FAE5", color: "#006145", text: "Completed" },
      Pending: { bg: "#FFFED8", color: "#687000", text: "Pending" },
      "Late Cancel": { bg: "#FEE9C6", color: "#973B00", text: "Late Cancel" },
      "No Show": { bg: "#FEE4E6", color: "#ED3A3A", text: "No Show" },
      Missed: { bg: "#FEE4E6", color: "#ED3A3A", text: "No Show" },
      Cancel: { bg: "#EDE9FE", color: "#5D0EC1", text: "Cancel" },
      Canceled: { bg: "#EDE9FE", color: "#5D0EC1", text: "Cancel" },
      "User Cancel": {
        bg: "#EDE9FE",
        color: "#5D0EC1",
        text: "User Cancel"
      },
      "Provider Cancel": {
        bg: "#C6E0FE",
        color: "#002697",
        text: "Provider Cancel"
      },
      "Admin Cancel": {
        bg: "#000000",
        color: "#FFFFFF",
        text: "Admin Cancel",
      },
    };

    const config = statusConfig[actualStatus] ?? {
      bg: "#FEF3C7",
      color: "#92400E",
      text: actualStatus ?? "Pending",
      variant: "secondary",
    };

    return (
      <Badge
        bg={config.bg}
        pill
        style={{
          backgroundColor: config.bg,
          color: config.color,
          fontWeight: "500",
          height: "19px",
          width: "92px",
        }}>
        {config.text}
      </Badge>
    );
  };

  const handleRangeSelect = (dates) => {
    const [start, end] = dates;

    setSelectedEndDate(end);
    setSelectedStartDate(start);

    if (end) setShowDatePicker(false);
  };

  const handleViewDetails = async (session) => {
    try {
      const appointmentId = session._id || session.id;

      if (!appointmentId) {
        toast.error("Appointment ID is missing");
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/payments/get-by-session/${appointmentId}`,
          getConfigWithToken(user.user?.token)
        );

        if (response.data.success && response.data.hasPayment) {
          // Store payment data in state
          setPaymentDataBySession((prev) => ({
            ...prev,
            [appointmentId]: response.data.data,
          }));
        } else {
          // No payment found - set empty payment data
          setPaymentDataBySession((prev) => ({
            ...prev,
            [appointmentId]: null,
          }));
        }
      } catch (paymentError) {
        // If payment fetch fails, set empty payment data and continue
        console.error("Error fetching payment details:", paymentError);
        setPaymentDataBySession((prev) => ({
          ...prev,
          [appointmentId]: null,
        }));
      }

      // Always show the modal regardless of payment status
      setSelectedSessionForModal(session);
      setShowDetailsModal(true);
    } catch (error) {
      console.error("Error in handleViewDetails:", error);
      toast.error("Error loading session details");
    }
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedSessionForModal(null);
  };

  const handleCompleteNote = (session) => {
    const fromPath = `${location.pathname}${location.search}`;

    // Check appointmentType - if Follow-Up, navigate to follow-up route
    if (session?.appointmentType === "Follow-Up") {
      navigate("/follow-up", {
        state: { session, from: fromPath },
      });
    } else {
      // For initial appointments, use the existing flow
      navigate("/session-details", {
        state: { session, from: fromPath },
      });
    }
  };

  const handleAddAddendum = async (session) => {
    const fromPath = `${location.pathname}${location.search}`;

    // Check if latest addendum entry can be edited (addendumSubmit: true, addendumSigned: false)
    let editExistingAddendum = false;
    const sessionId = session._id || session.id;

    if (sessionId) {
      // Try to get sessionDetail from state first
      let sessionDetail = sessionHistoryDetailsBySession[sessionId];

      // If not in state, fetch it
      if (!sessionDetail) {
        sessionDetail = await fetchSessionHistoryDetailBySessionId(sessionId);
      }

      // Check latest addendum entry
      if (sessionDetail?.addendumData && Array.isArray(sessionDetail.addendumData) && sessionDetail.addendumData.length > 0) {
        const latestIndex = sessionDetail.addendumData.length - 1;
        const latestAddendum = sessionDetail.addendumData[latestIndex];

        if (latestAddendum &&
          latestAddendum.addendumSubmit === true &&
          latestAddendum.addendumSigned === false) {
          editExistingAddendum = true;
        }
      }
    }

    // Check appointmentType - if Follow-Up, navigate to follow-up route
    if (session?.appointmentType === "Follow-Up") {
      navigate("/follow-up", {
        state: {
          session,
          from: fromPath,
          openAddendum: true,
          editExistingAddendum: editExistingAddendum
        },
      });
    } else {
      // For initial appointments, use the existing flow
      navigate("/session-details", {
        state: {
          session,
          from: fromPath,
          openAddendum: true,
          editExistingAddendum: editExistingAddendum
        },
      });
    }
  };

  const handleMarkNoShow = (session) => {
    setSelectedSessionForNoShow(session);
    setShowNoShowModal(true);
  };

  const closeNoShowModal = () => {
    setShowNoShowModal(false);
    setSelectedSessionForNoShow(null);
  };

  const handleWaiveFee = async () => {
    try {
      if (!selectedSessionForNoShow) {
        toast.error("Session not found");
        closeNoShowModal();
        return;
      }

      const appointmentId = selectedSessionForNoShow._id || selectedSessionForNoShow.id;

      if (!appointmentId) {
        toast.error("Appointment ID is missing");
        closeNoShowModal();
        return;
      }

      // Update appointment status to "No Show" with waive_fee flag
      const updateResponse = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/appointment/update-status/${appointmentId}`,
        {
          status: "No Show",
          waive_fee: true,
        },
        getConfigWithToken(user.user?.token)
      );

      if (updateResponse.data.success) {
        toast.success("Fee waived and session marked as No Show");
        closeNoShowModal();
        fetchSessions();
      } else {
        toast.error("Failed to update session status");
        closeNoShowModal();
      }
    } catch (error) {
      console.error("Error waiving fee:", error);
      toast.error(error?.response?.data?.message || "Failed to waive fee");
      closeNoShowModal();
    }
  };

  const handleApplyFee = async () => {
    try {
      if (!selectedSessionForNoShow) {
        toast.error("Session not found");
        closeNoShowModal();
        return;
      }

      const appointmentId = selectedSessionForNoShow._id || selectedSessionForNoShow.id;

      if (!appointmentId) {
        toast.error("Appointment ID is missing");
        closeNoShowModal();
        return;
      }

      // Update appointment status to "No Show" with no_show_fee flag
      const updateResponse = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/appointment/update-status/${appointmentId}`,
        {
          status: "No Show",
          no_show_fee: true,
        },
        getConfigWithToken(user.user?.token)
      );
      if (updateResponse.data.success) {
        toast.success("No-show fee applied and session marked as No Show");
        closeNoShowModal();
        fetchSessions();
      } else {
        toast.error("Failed to update session status");
        closeNoShowModal();
      }
    } catch (error) {
      console.error("Error applying fee:", error);
      toast.error(error?.response?.data?.message || "Failed to apply fee");
      closeNoShowModal();
    }
  };

  const handleMarkCanceled = (session) => {
    setSelectedSessionForCancel(session);
    setShowCancelModal(true);
  };

  const handleApplyLateCancelFee = async (session) => {
    try {
      if (!session) {
        toast.error("Session not found");
        return;
      }

      const appointmentId = session._id || session.id;

      if (!appointmentId) {
        toast.error("Appointment ID is missing");
        return;
      }

      // Update late cancel fee status
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/appointment/update-late-cancel-fee-status/${appointmentId}`,
        {},
        getConfigWithToken(user.user?.token)
      );
      if (response.data.success) {
        toast.success("Late cancel fee applied successfully");
        fetchSessions();
      } else {
        toast.error(response.data.message || "Failed to apply late cancel fee");
      }
    } catch (error) {
      console.error("Error applying late cancel fee:", error);
      toast.error(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to apply late cancel fee"
      );
    }
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
    setSelectedSessionForCancel(null);
  };

  const handleConfirmCancel = async () => {
    try {
      if (!selectedSessionForCancel) {
        toast.error("Session not found");
        return;
      }

      const appointmentId = selectedSessionForCancel._id || selectedSessionForCancel.id;

      if (!appointmentId) {
        toast.error("Appointment ID is missing");
        return;
      }
      const updateResponse = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/appointment/update-status/${appointmentId}`,
        {
          status: "Canceled by Provider",
        },
        getConfigWithToken(user.user?.token)
      );

      if (updateResponse.data.success) {
        toast.success("Session marked as canceled");
        closeCancelModal();
        fetchSessions();
      } else {
        toast.error("Failed to update session status");
      }
    } catch (error) {
      console.error("Error canceling session:", error);
      toast.error(error?.response?.data?.message || "Failed to cancel session");
    }
  };

  const calculateDropdownPosition = useCallback((buttonRect, dropdownHeight = 160, dropdownWidth = 160) => {
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    let top, left;
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      top = buttonRect.top - dropdownHeight - 4;
      if (top < 0) {
        top = 4;
      }
    } else {
      top = buttonRect.bottom + 4;
      if (top + dropdownHeight > viewportHeight) {
        top = viewportHeight - dropdownHeight - 4;
        if (top < buttonRect.top - dropdownHeight) {
          top = Math.max(4, buttonRect.top - dropdownHeight - 4);
        }
      }
    }

    left = buttonRect.left + buttonRect.width / 2;
    if (left - dropdownWidth / 2 < 0) {
      left = dropdownWidth / 2 + 4;
    } else if (left + dropdownWidth / 2 > viewportWidth) {
      left = viewportWidth - dropdownWidth / 2 - 4;
    }

    return { top, left };
  }, []);

  const fetchSessionHistoryDetailBySessionId = async (sessionId) => {
    try {
      if (!sessionId) {
        return null;
      }

      if (sessionHistoryDetailsBySession[sessionId]) {
        return sessionHistoryDetailsBySession[sessionId];
      }

      setLoadingSessionDetails((prev) => ({
        ...prev,
        [sessionId]: true,
      }));

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/sessionHistory/get-by-session/${sessionId}`,
        getConfigWithToken(user.user?.token)
      );

      if (response.data.success) {
        const detail = response.data.data;

        setSessionHistoryDetailsBySession((prev) => ({
          ...prev,
          [sessionId]: detail,
        }));

        setLoadingSessionDetails((prev) => {
          const newState = { ...prev };
          delete newState[sessionId];
          return newState;
        });

        return detail;
      }
    } catch (error) {
      console.error("-> -> fetchSessionHistoryDetailBySessionId -> error->", error);
      setLoadingSessionDetails((prev) => {
        const newState = { ...prev };
        delete newState[sessionId];
        return newState;
      });
      return null;
    }
  };

  const handleDownloadNote = async (session) => {
    if (!session?._id) {
      toast.error("Session ID is missing");
      return;
    }

    try {
      let sessionDetail = sessionHistoryDetailsBySession[session._id];
      if (!sessionDetail) {
        toast.info("Loading session details...");
        sessionDetail = await fetchSessionHistoryDetailBySessionId(session._id);
      }

      if (!sessionDetail) {
        toast.error("Failed to load session details");
        return;
      }

      const clientName = `${session.user?.firstname || ""} ${session.user?.lastname || ""}`.trim();
      const providerName = `${session.health_specialist?.firstname || ""} ${session.health_specialist?.lastname || ""}`.trim();
      const appointmentDate = session.appointmentdate || session.appointmenttime;

      const getSignedDateFromDB = () => {
        if (!sessionDetail?.signedAt) return null;
        return new Date(sessionDetail.signedAt).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric"
        });
      };

      const getSignedTimeFromDB = () => {
        if (!sessionDetail?.signedAt) return null;
        return new Date(sessionDetail.signedAt).toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true
        });
      };

      const formatDateTime = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      };

      const appointmentTime = session.appointmenttime ? formatDateTime(session.appointmenttime) : "N/A";

      let addendumDataArray = [];
      if (sessionDetail?.addendumData) {
        addendumDataArray = Array.isArray(sessionDetail.addendumData)
          ? sessionDetail.addendumData
          : [];
      }

      const calculateTotalDuration = () => {
        if (sessionDetail?.totalDuration) {
          return sessionDetail.totalDuration;
        }
        if (session.appointmenttime && session.appointmentEndTime) {
          const start = new Date(session.appointmenttime);
          const end = new Date(session.appointmentEndTime);
          const diffMs = end - start;
          const diffMinutes = Math.floor(diffMs / 60000);
          return diffMinutes;
        }
        return 0;
      };

      const totalDuration = calculateTotalDuration();

      // Check appointmentType - if Follow-Up, use FollowUpPDFGenerator
      if (session?.appointmentType === "Follow-Up") {
        // Helper function to parse time string and convert to minutes
        const getMinutesFromTimeString = (timeString) => {
          if (!timeString) return null;

          // Parse time string like "2:30 PM" or "14:30"
          const timeRegex = /(\d{1,2}):(\d{2})\s*(AM|PM)?/i;
          const match = timeString.match(timeRegex);

          if (!match) return null;

          let hour = parseInt(match[1], 10);
          const minute = parseInt(match[2], 10);
          const period = match[3]?.toUpperCase();

          if (period === "PM" && hour !== 12) {
            hour += 12;
          } else if (period === "AM" && hour === 12) {
            hour = 0;
          }

          return hour * 60 + minute;
        };

        // Calculate derivedDurationMinutes from timeIn and timeOut
        const calculateDerivedDurationMinutes = () => {
          const timeIn = sessionDetail?.timeIn;
          const timeOut = sessionDetail?.timeOut;

          if (!timeIn || !timeOut) {
            // Fallback to totalDuration if timeIn/timeOut not available
            return totalDuration || null;
          }

          const startMinutes = getMinutesFromTimeString(timeIn);
          const endMinutes = getMinutesFromTimeString(timeOut);

          if (startMinutes === null || endMinutes === null) {
            return totalDuration || null;
          }

          let diff = endMinutes - startMinutes;
          if (diff < 0) {
            diff += 24 * 60; // Handle next day
          }

          return diff;
        };

        const derivedDurationMinutes = calculateDerivedDurationMinutes();
        const clientDOB = session?.user?.dob || sessionDetail?.user?.dob;

        const generatePDF = FollowUpPDFGenerator({
          sessionFormData: sessionDetail,
          clientName,
          providerName,
          providerCredentials: formatCredentials(session?.health_specialist?.credentials) || "LPC",
          appointmentDate,
          appointmentTime,
          clientDOB: clientDOB ? formatDate(clientDOB) : "N/A",
          isSigned: sessionDetail?.isSigned === true || sessionDetail?.isSigned === "true",
          getSignedDateFromDB,
          getSignedTimeFromDB,
          formatDate,
          derivedDurationMinutes,
          addendumDataArray,
        });

        generatePDF();
      } else {
        // For initial appointments, use the existing SessionHistoryPDFGenerator
        const generatePDF = SessionHistoryPDFGenerator({
          sessionFormData: sessionDetail,
          clientName,
          providerName,
          providerCredentials: formatCredentials(session?.health_specialist?.credentials) || "LPC",
          appointmentDate,
          appointmentTime,
          isSigned: sessionDetail?.isSigned === true || sessionDetail?.isSigned === "true",
          signedDateFromDB: getSignedDateFromDB(),
          signedTimeFromDB: getSignedTimeFromDB(),
          formatDate,
          formatDateTime,
          totalDuration,
          addendumData: addendumDataArray,
          clientDOB: session?.user?.dob || sessionDetail?.user?.dob,
        });

        generatePDF();
      }
    } catch (error) {
      console.error("Error downloading note:", error);
      toast.error("An error occurred while downloading the note.");
    }
  };

  const handleDropdownToggle = useCallback((sessionId, event) => {
    if (openDropdownId === sessionId) {
      setOpenDropdownId(null);
      dropdownButtonRef.current = null;
    } else {
      const button = event.currentTarget;
      dropdownButtonRef.current = button;
      const rect = button.getBoundingClientRect();

      const position = calculateDropdownPosition(rect);
      setDropdownPosition(position);
      setOpenDropdownId(sessionId);

      fetchSessionHistoryDetailBySessionId(sessionId);
    }
  }, [openDropdownId, calculateDropdownPosition, user.user?.token]);

  const handleDropdownItemClick = (handler, session) => {
    handler(session);
    setOpenDropdownId(null);
    dropdownButtonRef.current = null;
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const filteredSessions = getFilteredSessions();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedEndDate, selectedStartDate, statusFilter, statusTypeFilter]);

  useEffect(() => {
    fetchSessions();
  }, [
    debouncedSearch,
    selectedEndDate,
    selectedStartDate,
    currentPage,
    appointmentsPerPage,
    statusFilter,
    statusTypeFilter,
  ]);

  useEffect(() => {
    if (showAllSessions) {
      setPaginateAppointments(filteredSessions);
    } else {
      setPaginateAppointments(filteredSessions.slice(0, appointmentsPerPage));
    }
  }, [appointmentsPerPage, filteredSessions, showAllSessions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !event.target.closest('.session-dropdown-button') && !event.target.closest('.session-dropdown-menu')) {
        setOpenDropdownId(null);
        dropdownButtonRef.current = null;
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && openDropdownId) {
        setOpenDropdownId(null);
        dropdownButtonRef.current = null;
      }
    };

    let scrollTimeout;
    const handleScroll = () => {
      if (openDropdownId && dropdownButtonRef.current) {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          const rect = dropdownButtonRef.current.getBoundingClientRect();
          const position = calculateDropdownPosition(rect);
          setDropdownPosition(position);
        }, 10);
      }
    };

    const handleResize = () => {
      if (openDropdownId && dropdownButtonRef.current) {
        const rect = dropdownButtonRef.current.getBoundingClientRect();
        const position = calculateDropdownPosition(rect);
        setDropdownPosition(position);
      }
    };

    if (openDropdownId) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      return () => {
        clearTimeout(scrollTimeout);
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [openDropdownId, calculateDropdownPosition]);

  return (
    <div className="app-page session-history-container">
      <ToastContainer />

      {!showSessionDetail ? (
        <Card
          style={{
            border: "none",
            borderRadius: "10px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            height: "100%",
            overflow: "hidden",
            maxWidth: "100%",
            width: "100%",
            boxSizing: "border-box",
          }}>
          <Card.Body className="p-0" style={{ overflow: "hidden", maxWidth: "100%" }}>
            <div className="app-page-toolbar mb-0" style={{ width: "100%", gap: "10px" }}>
              <div className="flex-shrink-0">
                <h3 className="app-page-title">
                  Session History
                </h3>
              </div>

              <div className="d-flex gap-2 flex-nowrap align-items-center flex-shrink-0 app-page-toolbar-filters">
                <div className="app-filter-search-wrap" style={{ minWidth: "180px", width: "180px" }}>
                  <FaSearch className="app-filter-search-icon" />
                  <input
                    className="form-control app-filter-search"
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search"
                    style={{
                      width: "100%",
                      paddingRight: search ? "30px" : undefined,
                    }}
                    type="text"
                    value={search}
                  />

                  {search && (
                    <i
                      className="fas fa-times position-absolute"
                      onClick={clearSearchFilter}
                      style={{
                        color: "#dc3545",
                        cursor: "pointer",
                        fontSize: "12px",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 2,
                      }}
                    />
                  )}
                </div>

                <div className="position-relative date-picker-container" style={{ minWidth: "140px" }}>
                  <Button
                    className="custom-date-button"
                    onClick={() => setShowDatePicker(!showDatePicker)}
                    variant="outline-secondary"
                    style={{
                      fontSize: "14px",
                      padding: "6px 12px",
                      height: "45px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}>
                    {selectedStartDate && selectedEndDate
                      ? `${formatDate(selectedStartDate)} - ${formatDate(
                        selectedEndDate
                      )}`
                      : selectedStartDate
                        ? formatDate(selectedStartDate)
                        : "Date"}
                  </Button>

                  {showDatePicker && (
                    <div
                      className="position-absolute"
                      style={{ left: 0, top: "100%", zIndex: 1000 }}>
                      <DatePicker
                        endDate={selectedEndDate}
                        inline
                        isClearable={true}
                        maxDate={new Date()}
                        onChange={handleRangeSelect}
                        selectsRange
                        startDate={selectedStartDate}
                      />
                    </div>
                  )}
                </div>

                <select
                  className="form-select custom-select app-filter-select"
                  onChange={(e) => setStatusTypeFilter(e.target.value)}
                  value={statusTypeFilter}
                  style={{
                    fontSize: "14px",
                    padding: "6px 40px 6px 12px",
                    height: "45px",
                    minWidth: "120px",
                    appearance: "none",
                    WebkitAppearance: "none",
                    backgroundPosition: "right 14px center",
                    backgroundSize: "12px 12px",
                    backgroundRepeat: "no-repeat",
                  }}>
                  <option value="All">Type</option>

                  <option value="initial">Initial Assessment</option>

                  <option value="followUp">Follow-Up</option>

                </select>

                <select
                  className="form-select custom-select app-filter-select"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  value={statusFilter}
                  style={{
                    fontSize: "14px",
                    padding: "6px 40px 6px 12px",
                    height: "45px",
                    minWidth: "130px",
                    appearance: "none",
                    WebkitAppearance: "none",
                    backgroundPosition: "right 14px center",
                    backgroundSize: "12px 12px",
                    backgroundRepeat: "no-repeat",
                  }}>
                  <option value="All">All Statuses</option>

                  <option value="Canceled">Canceled</option>

                  <option value="Completed">Completed</option>

                  <option value="Missed">Missed</option>
                </select>
              </div>
            </div>

            <div className="table-responsive" style={{ overflowX: "auto", overflowY: "visible", width: "100%", maxWidth: "100%" }}>
              {sessionsLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />

                  <div className="mt-3 text-muted">Loading sessions...</div>
                </div>
              ) : (
                <>
                  <Table hover style={{ marginBottom: 0, width: "100%", tableLayout: "auto" }}>
                    <thead
                      style={{
                        backgroundColor: "#064260",
                        borderRadius: "0",
                        color: "white",
                      }}>
                      <tr>
                        <th
                          style={{
                            
                            padding: "12px 10px",
                            textAlign: "center",
                            verticalAlign: "middle",
                            whiteSpace: "nowrap",
                            fontSize: "13px",
                          }}>
                          <i className="fas fa-hashtag me-2"></i>Session ID
                        </th>

                        <th style={{ padding: "12px 10px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap", fontSize: "13px" }}>
                          <i className="fas fa-user me-2"></i>Client
                        </th>

                        <th style={{ padding: "12px 10px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap", fontSize: "13px" }}>
                          <i className="fas fa-user-md me-2"></i>Type
                        </th>

                        <th style={{ padding: "12px 10px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap", fontSize: "13px" }}>
                          <i className="fas fa-calendar me-2"></i>Date
                        </th>

                        <th style={{ padding: "12px 10px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap", fontSize: "13px" }}>
                          <i className="fas fa-clock me-2"></i>Scheduled Time
                        </th>

                        <th style={{ padding: "12px 10px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap", fontSize: "13px" }}>
                          <i className="fas fa-clock me-2"></i>Actual Time
                        </th>

                        <th style={{ padding: "12px 10px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap", fontSize: "13px" }}>
                          <i className="fas fa-info-circle me-2"></i>Status
                        </th>

                        <th
                          style={{
                            padding: "12px 10px",
                            textAlign: "center",
                            verticalAlign: "middle",
                            whiteSpace: "nowrap",
                            fontSize: "13px",
                          }}>
                          <i className="fas fa-cogs me-2"></i>Action
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {paginateAppointments.length > 0 ? (
                        paginateAppointments.map((session) => {
                          return (
                            <tr
                              key={session._id}
                              style={{ borderBottom: "1px solid #f8f9fa" }}>
                              <td
                                style={{
                                  color: "#2C5F7C",
                                  fontWeight: "600",
                                  padding: "12px 10px",
                                  textAlign: "center",
                                  verticalAlign: "middle",
                                  whiteSpace: "nowrap",
                                  fontSize: "13px",
                                }}>
                                {session.appointment_id}
                              </td>

                              <td style={{ padding: "12px 10px", fontSize: "13px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                                {session?.user?.firstname}{" "}
                                {session?.user?.lastname}
                              </td>

                              <td style={{ padding: "12px 10px", fontSize: "13px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                                {session?.appointmentType === "initial" ? "Initial Assessment" : "Follow-Up"}
                                {/* {session?.type || "-"} */}
                              </td>

                              <td style={{ padding: "12px 10px", fontSize: "13px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                                {session.appointmenttime
                                  ? formatDate(session.appointmenttime, "date")
                                  : session.appointmentdate
                                    ? formatDate(session.appointmentdate, "date")
                                    : "N/A"}
                              </td>

                              <td style={{ padding: "12px 10px", fontSize: "13px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                                {session.appointmenttime
                                  ? `${formatDate(
                                    session.appointmenttime,
                                    "time"
                                  )} - ${formatDate(
                                    session.appointmentEndTime,
                                    "time"
                                  )}`
                                  : "N/A"}
                              </td>

                              <td style={{ padding: "12px 10px", fontSize: "13px", textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                                {(() => {
                                  const sessionId = session._id || session.id;
                                  const sessionDetail = sessionHistoryDetailsBySession[sessionId];

                                  const formatTimeString = (timeStr) => {
                                    if (!timeStr) return "";

                                    // If already formatted (contains AM/PM), return as it is
                                    if (typeof timeStr === "string" && timeStr.toLowerCase().includes("am") || timeStr.toLowerCase().includes("pm")) {
                                      return timeStr;
                                    }

                                    // If HH:MM or HH:MM:SS → return without adding AM/PM
                                    if (typeof timeStr === "string" && timeStr.includes(":")) {
                                      const parts = timeStr.split(":");
                                      if (parts.length >= 2) {
                                        return `${parts[0]}:${parts[1]}`; // no conversion
                                      }
                                      return timeStr;
                                    }

                                    // If date format → use your formatDate utility with time only
                                    try {
                                      return formatDate(timeStr, "time"); // assuming utility already includes AM/PM
                                    } catch (e) {
                                      return timeStr;
                                    }
                                  };

                                  if (sessionDetail?.timeIn && sessionDetail?.timeOut) {
                                    return `${formatTimeString(sessionDetail.timeIn)} - ${formatTimeString(
                                      sessionDetail.timeOut
                                    )}`;
                                  }

                                  return "-";
                                })()}
                              </td>

                              <td style={{ padding: "12px 10px", fontSize: "13px", textAlign: "center", verticalAlign: "middle" }}>
                                <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                  {getStatusBadge(session)}
                                </div>
                              </td>

                              <td style={{ padding: "12px 10px", fontSize: "13px", textAlign: "center", verticalAlign: "middle" }}>
                                <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                  <button
                                    className="session-dropdown-button"
                                    onClick={(e) => handleDropdownToggle(session._id, e)}
                                    style={{
                                      backgroundColor: "#27B99A",
                                      border: "none",
                                      borderRadius: "6px",
                                      width: "66px",
                                      height: "26px",
                                      padding: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      minWidth: "66px",
                                      cursor: "pointer",
                                    }}>
                                    <span
                                      style={{
                                        color: "#ffffff",
                                        fontSize: "24px",
                                        fontWeight: "bold",
                                        margin: 0,
                                        padding: 0,
                                        lineHeight: 1,
                                        display: "block",
                                      }}>
                                      ⋯
                                    </span>
                                  </button>
                                </div>
                              </td>

                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            className="text-center text-muted py-5"
                            colSpan="7">
                            <div style={{ color: "#6c757d", fontSize: "18px" }}>
                              <i
                                className="fas fa-inbox fa-2x mb-3"
                                style={{ color: "#dee2e6" }}></i>
                              <br />

                              {sessions.length === 0 ? (
                                <>
                                  <strong>No sessions found</strong>

                                  <br />

                                  <small className="text-muted">
                                    No session history available.
                                  </small>
                                </>
                              ) : selectedEndDate ||
                                selectedStartDate ||
                                statusFilter !== "All" ||
                                (search && search.trim() !== "") ? (
                                <>
                                  <strong>
                                    No sessions found for the selected filters
                                  </strong>

                                  <br />

                                  <small className="text-muted">
                                    Try adjusting your search, date, or status
                                    filters.
                                  </small>
                                </>
                              ) : (
                                <>
                                  <strong>No sessions available</strong>

                                  <br />

                                  <small className="text-muted">
                                    Unable to load session data.
                                  </small>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>

                  {showAllSessions ? (
                    <div className="d-flex justify-content-end align-items-center py-3 px-4">
                      <Button
                        onClick={() => {
                          setShowAllSessions(false);
                          setPaginateAppointments(
                            filteredSessions.slice(0, appointmentsPerPage)
                          );
                        }}
                        size="sm"
                        style={{
                          backgroundColor: "#27B99A",
                          border: "none",
                          borderRadius: "6px",
                          fontWeight: "500",
                          padding: "6px 16px",
                        }}
                        variant="success">
                        View Less
                      </Button>
                    </div>
                  ) : (
                    <TablePaginationBar
                      currentPage={currentPage}
                      totalItems={totalSessions}
                      rowsPerPage={appointmentsPerPage}
                      onPageChange={paginate}
                      onRowsPerPageChange={(n) => {
                        setAppointmentsPerPage(n);
                        setCurrentPage(1);
                      }}
                      rowsPerPageOptions={[10, 25, 50]}
                      leftExtra={
                        <Button
                          onClick={() => {
                            setPaginateAppointments(filteredSessions);
                            setShowAllSessions(true);
                          }}
                          size="sm"
                          style={{
                            backgroundColor: "#27B99A",
                            border: "none",
                            borderRadius: "6px",
                            fontWeight: "500",
                            padding: "6px 16px",
                          }}
                          variant="success">
                          View All
                        </Button>
                      }
                    />
                  )}
                </>
              )}
            </div>
          </Card.Body>
        </Card>
      ) : (
        <div>
          <div className="mb-3">
            <Button
              onClick={closeSessionDetail}
              style={{
                alignItems: "center",
                backgroundColor: "transparent",
                border: "none",
                color: "#27B99A",
                display: "flex",
                fontSize: "16px",
                fontWeight: "500",
                gap: "8px",
                justifyContent: "center",
                padding: "8px 0",
              }}
              variant="button">
              <svg
                fill="none"
                height="28"
                viewBox="0 0 28 28"
                width="28"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M27.3333 14.0001C27.3333 6.64808 21.3519 0.666748 13.9999 0.666747C6.64792 0.666747 0.666588 6.64808 0.666587 14.0001C0.666587 21.3521 6.64792 27.3334 13.9999 27.3334C21.3519 27.3334 27.3333 21.3521 27.3333 14.0001ZM8.11459 14.0001L15.7239 6.39075L17.6093 8.27608L11.8853 14.0001L17.6093 19.7241L15.7239 21.6094L8.11459 14.0001Z"
                  fill="#27B99A"
                />
              </svg>
              Back
            </Button>
          </div>

          <Card
            style={{
              backgroundColor: "#ffffff",
              border: "none",
              height: "100%",
              maxWidth: "80%",
              textAlign: "start",
            }}>
            <Card.Body style={{ padding: "32px" }}>
              <h4
                style={{
                  backgroundColor: "#ffffff",
                  color: "#000000",
                  fontFamily: "Poppins",
                  fontWeight: "700",
                  justifyContent: "start",
                  marginBottom: "32px",
                }}>
                Session: {selectedSession?.appointment_id || "N/A"}
              </h4>

              <div className="mb-4">
                <Row>
                  <Col md={6}>
                    <div className="mb-3 d-flex align-items-center gap-2">
                      <span
                        style={{
                          color: "#000000",
                          fontSize: "14px",
                          fontWeight: "400",
                        }}>
                        Client
                      </span>

                      <div
                        style={{
                          backgroundColor: "#FAFAFA",
                          color: "#000000",
                          fontSize: "12px",
                          fontWeight: "500",
                          padding: "8px 12px",
                        }}>
                        {selectedSession?.user?.firstname}{" "}
                        {selectedSession?.user?.lastname}
                      </div>
                    </div>

                    <div className="mb-3 d-flex align-items-center gap-2">
                      <span
                        style={{
                          color: "#000000",
                          fontSize: "14px",
                          fontWeight: "400",
                        }}>
                        Date
                      </span>

                      <div
                        style={{
                          backgroundColor: "#FAFAFA",
                          color: "#000000",
                          fontSize: "12px",
                          fontWeight: "500",
                          padding: "8px 12px",
                        }}>
                        {selectedSession?.appointmenttime
                          ? formatDate(selectedSession.appointmenttime, "date")
                          : selectedSession?.appointmentdate
                            ? formatDate(selectedSession.appointmentdate, "date")
                            : "N/A"}
                      </div>
                    </div>

                    <div className="mb-3 d-flex align-items-center gap-2">
                      <span
                        style={{
                          color: "#000000",
                          fontSize: "14px",
                          fontWeight: "400",
                        }}>
                        Session Length
                      </span>

                      <div
                        style={{
                          backgroundColor: "#FAFAFA",
                          color: "#000000",
                          fontSize: "12px",
                          fontWeight: "500",
                          padding: "8px 12px",
                        }}>
                        {selectedSession?.sessionLength ||
                          selectedSession?.duration}
                        m
                      </div>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="mb-3 d-flex align-items-center gap-2">
                      <span
                        style={{
                          color: "#000000",
                          fontSize: "14px",
                          fontWeight: "400",
                        }}>
                        Provider
                      </span>

                      <div
                        style={{
                          backgroundColor: "#FAFAFA",
                          color: "#000000",
                          fontSize: "12px",
                          fontWeight: "500",
                          padding: "8px 12px",
                        }}>
                        {selectedSession?.health_specialist?.firstname}{" "}
                        {selectedSession?.health_specialist?.lastname}
                      </div>
                    </div>

                    <div className="mb-3 d-flex align-items-center gap-2">
                      <span
                        style={{
                          color: "#000000",
                          fontSize: "14px",
                          fontWeight: "400",
                        }}>
                        Scheduled Time
                      </span>

                      <div
                        style={{
                          backgroundColor: "#FAFAFA",
                          color: "#000000",
                          fontSize: "12px",
                          fontWeight: "500",
                          padding: "8px 12px",
                        }}>
                        {selectedSession?.appointmenttime
                          ? formatDate(selectedSession.appointmenttime, "time")
                          : "N/A"}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              <div
                style={{
                  backgroundColor: "#E5E5E5",
                  height: "1px",
                  marginBottom: "32px",
                  width: "100%",
                }}
              />

              {summaryLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" />

                  <div className="mt-3 text-muted">
                    Loading session summary...
                  </div>
                </div>
              ) : summaryData ? (
                <div>
                  {summaryData.content && (
                    <div className="mb-4">
                      <h5
                        style={{
                          color: "#000000",
                          fontFamily: "Poppins",
                          fontSize: "20px",
                          fontWeight: "600",
                          marginBottom: "16px",
                        }}>
                        Session Summary
                      </h5>

                      <div
                        style={{
                          color: "#000000",
                          whiteSpace: "pre-wrap",
                        }}>
                        {summaryData.content}
                      </div>
                    </div>
                  )}

                  {summaryData.content && (
                    <div
                      style={{
                        backgroundColor: "#E5E5E5",
                        height: "1px",
                        marginBottom: "32px",
                        width: "100%",
                      }}
                    />
                  )}
                  {console.log(summaryData, "summaryData")}
                  {summaryData.actionItems && (
                    <div>
                      <h5
                        style={{
                          color: "#000000",
                          fontFamily: "Poppins",
                          fontSize: "20px",
                          fontWeight: "600",
                          marginBottom: "16px",
                        }}>
                        Action items
                      </h5>

                      <div
                        style={{
                          color: "#000000",
                          whiteSpace: "pre-wrap",
                        }}>
                        {summaryData.actionItems}
                      </div>
                    </div>
                  )}

                  {!summaryData.content && !summaryData.actionItems && (
                    <div className="text-center py-4">
                      <i
                        className="fas fa-file-alt fa-3x mb-3"
                        style={{ color: "#dee2e6" }}></i>

                      <h5 style={{ color: "#6c757d" }}>No Summary Available</h5>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <i
                    className="fas fa-exclamation-triangle fa-3x mb-3"
                    style={{ color: "#ffc107" }}></i>

                  <h5 style={{ color: "#6c757d" }}>Error Loading Summary</h5>

                  <p className="text-muted">
                    Unable to load the session summary. Please try again.
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      )}

      {/* View Details Modal */}
      {showDetailsModal && selectedSessionForModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "stretch",
            justifyContent: "flex-end",
            zIndex: 10001,
            padding: 0,
          }}
          onClick={closeDetailsModal}>
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "0",
              width: "550px",
              maxWidth: "90vw",
              height: "100vh",
              overflowY: "auto",
              overflowX: "hidden",
              position: "relative",
              margin: 0,
              boxShadow: "-4px 0 20px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                padding: "24px 24px 16px 24px",
                borderBottom: "1px solid #e5e5e5",
              }}>
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#000000",
                  margin: 0,
                  fontFamily: "Poppins, sans-serif",
                }}>
                Session ID: {selectedSessionForModal.appointment_id || selectedSessionForModal._id || "N/A"}
              </h2>
              <button
                onClick={closeDetailsModal}
                style={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                  fontSize: "14px",
                  cursor: "pointer",
                  color: "#000000",
                  padding: "8px 16px",
                  fontWeight: "500",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#f5f5f5";
                  e.target.style.boxShadow = "0 2px 6px rgba(0,0,0,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#ffffff";
                  e.target.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
                }}>
                Close
              </button>
            </div>

            {/* Status Tags */}
            <div
              style={{
                padding: "16px 24px",
                display: "flex",
                gap: "8px",
                flexWrap: "wrap",
              }}>
              {/* Status Badge */}
              {(() => {
                const now = new Date();
                const appointmentTime = selectedSessionForModal.appointmenttime || selectedSessionForModal.appointmentdate;
                const appointmentEndTime = selectedSessionForModal.appointmentEndTime;

                let status = "Pending";
                if (appointmentTime) {
                  const appointmentDate = new Date(appointmentTime);
                  const endTime = appointmentEndTime ? new Date(appointmentEndTime) : null;

                  if (appointmentDate > now || (endTime && endTime > now)) {
                    status = "Pending";
                  } else {
                    status = getActualStatus(selectedSessionForModal);
                  }
                }

                const statusColors = {
                  "Completed": { bg: "#D0FAE5", color: "#006145" },
                  "Pending": { bg: "#FEF3C7", color: "#92400E" },
                  "Late Cancel": { bg: "#FED7AA", color: "#C2410C" },
                  "No Show": { bg: "#FEE4E6", color: "#ED3A3A" },
                  "Missed": { bg: "#FEE4E6", color: "#ED3A3A" },
                  "Cancel": { bg: "#EDE9FE", color: "#5D0EC1" },
                  "Canceled": { bg: "#EDE9FE", color: "#5D0EC1" },
                };

                const colors = statusColors[status] || { bg: "#FEF3C7", color: "#92400E" };

                return (
                  <span
                    style={{
                      backgroundColor: colors.bg,
                      color: colors.color,
                      padding: "6px 12px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: "500",
                    }}>
                    {status}
                  </span>
                );
              })()}

              {/* Session Type Badge */}
              <span
                style={{
                  backgroundColor: "#F3F4F6",
                  color: "#6B7280",
                  padding: "6px 12px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: "500",
                }}>
                {selectedSessionForModal?.type || selectedSessionForModal?.appointmentType === "initial" ? "Initial Assessment" : "Follow-Up"}
              </span>

              {/* Date/Time Badge */}
              <span
                style={{
                  backgroundColor: "#F3F4F6",
                  color: "#6B7280",
                  padding: "6px 12px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: "500",
                }}>
                {(() => {
                  const appointmentTime = selectedSessionForModal.appointmenttime || selectedSessionForModal.appointmentdate;
                  if (!appointmentTime) return "N/A";

                  const date = new Date(appointmentTime);

                  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
                  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

                  const dayName = dayNames[date.getDay()];
                  const month = monthNames[date.getMonth()];
                  const day = date.getDate();
                  const year = date.getFullYear();

                  // Format time from appointmentTime
                  const startTime = date.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  });

                  // Format time from appointmentEndTime
                  let endTimeStr = "";
                  if (selectedSessionForModal.appointmentEndTime) {
                    const endTime = new Date(selectedSessionForModal.appointmentEndTime);
                    endTimeStr = endTime.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    });
                  }

                  return `${dayName}, ${month} ${day}, ${year}${endTimeStr ? ` · ${startTime} - ${endTimeStr}` : ` · ${startTime}`}`;
                })()}
              </span>
            </div>

            <div
              style={{
                backgroundColor: "#ffffff",
                margin: "0 24px 24px 24px",
                padding: "24px",
                borderRadius: "8px",
                border: "1px solid #e5e5e5",
              }}>
              <div style={{ display: "flex", width: "100%", textAlign: "left" }}>

                <div style={{ width: "50%" }}>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#6B7280",
                      marginBottom: "8px",
                    }}>
                    Patient
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#000000",
                      marginBottom: "4px",
                    }}>
                    {selectedSessionForModal?.user?.firstname || ""}{" "}
                    {selectedSessionForModal?.user?.lastname || ""}
                  </div>
                  {selectedSessionForModal?.user?.dob && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6B7280",
                        marginBottom: "24px",
                      }}>
                      DOB: {formatDate(selectedSessionForModal.user.dob)}
                    </div>
                  )}
                  {!selectedSessionForModal?.user?.dob && (
                    <div style={{ marginBottom: "24px" }}></div>
                  )}
                </div>

                <div style={{ width: "50%" }}>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#6B7280",
                      marginBottom: "8px",
                    }}>
                    Session Type
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#000000",
                    }}>
                    {selectedSessionForModal?.type || (selectedSessionForModal?.appointmentType === "initial" ? "Initial Assessment" : "Follow-Up")}
                  </div>
                </div>
              </div>

              {(() => {
                const sessionId = selectedSessionForModal?._id || selectedSessionForModal?.id;
                const paymentData = paymentDataBySession[sessionId];
                const paymentMethod = paymentData?.paymentMethod || selectedSessionForModal?.paymentMethod || selectedSessionForModal?.user?.paymentMethod;

                // Only show Payment Method section if paymentMethod exists and is not empty
                if (!paymentMethod || paymentMethod.trim() === "") {
                  return null;
                }

                return (
                  <div style={{ display: "flex", width: "100%", textAlign: "left" }}>
                    <div style={{ width: "50%" }}>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#6B7280",
                          marginBottom: "8px",
                        }}>
                        Payment Method
                      </div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "#000000",
                        }}>
                        {paymentMethod}
                      </div>
                    </div>

                    {(() => {
                      if (paymentMethod === "Insurance") {
                        return (
                          <div style={{ width: "50%" }}>
                            <div
                              style={{
                                fontSize: "14px",
                                color: "#6B7280",
                                marginBottom: "8px",
                              }}>
                              Plan / Member ID
                            </div>
                            <div
                              style={{
                                fontSize: "16px",
                                fontWeight: "600",
                                color: "#000000",
                                marginBottom: "4px",
                              }}>
                              {selectedSessionForModal?.insurance?.planName ||
                                selectedSessionForModal?.user?.insurance?.planName ||
                                selectedSessionForModal?.planName ||
                                "CareFirst Blue Cross"}
                            </div>
                            {(selectedSessionForModal?.insurance?.memberId ||
                              selectedSessionForModal?.user?.insurance?.memberId ||
                              selectedSessionForModal?.memberId) && (
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "#6B7280",
                                  }}>
                                  ID: {selectedSessionForModal?.insurance?.memberId ||
                                    selectedSessionForModal?.user?.insurance?.memberId ||
                                    selectedSessionForModal?.memberId ||
                                    "1234567890"}
                                </div>
                              )}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {openDropdownId && (() => {
        const session = paginateAppointments.find(s => s._id === openDropdownId);
        if (!session) return null;

        const sessionId = session._id || session.id;
        const isLoading = loadingSessionDetails[sessionId];
        const hasData = sessionHistoryDetailsBySession[sessionId];

        return createPortal(
          <div
            ref={dropdownMenuRef}
            className="session-dropdown-menu"
            role="menu"
            aria-label="Session actions"
            style={{
              position: "fixed",
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              transform: "translateX(-50%)",
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              border: "1px solid #e0e0e0",
              padding: "4px 0",
              minWidth: "170px",
              zIndex: 10000,
              overflow: "visible",
              maxHeight: "none",
              pointerEvents: "auto",
              isolation: "isolate",
            }}
            onClick={(e) => e.stopPropagation()}>
            {(() => {
              // Show loader if data is loading and not available
              if (isLoading && !hasData) {
                return (
                  <div
                    style={{
                      fontSize: "14px",
                      padding: "12px 16px",
                      color: "#666666",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}>
                    <Spinner animation="border" size="sm" style={{ width: "16px", height: "16px" }} />
                    Loading...
                  </div>
                );
              }

              const isLateCancelled = session.status === "late_cancelled";
              const isNoShow = session.status === "No Show" || getActualStatus(session) === "No Show";
              const isCancel = session.status === "Cancel" || getActualStatus(session) === "Cancel";

              // If status is No Show or Cancel, show only View Details
              if (isNoShow || isCancel) {
                return (
                  <div
                    role="menuitem"
                    tabIndex={0}
                    onClick={() => handleDropdownItemClick(handleViewDetails, session)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleDropdownItemClick(handleViewDetails, session);
                      }
                    }}
                    style={{
                      fontSize: "14px",
                      padding: "8px 16px",
                      color: "#000000",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = "#e7f3ff"}
                    onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}>
                    View Details
                  </div>
                );
              }

              if (isLateCancelled) {
                const hasLateCancelFee = session.late_cancel_fee === true;

                return (
                  <>
                    <div
                      role="menuitem"
                      tabIndex={0}
                      onClick={() => handleDropdownItemClick(handleViewDetails, session)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleDropdownItemClick(handleViewDetails, session);
                        }
                      }}
                      style={{
                        fontSize: "14px",
                        padding: "8px 16px",
                        color: "#000000",
                        cursor: "pointer",
                        transition: "background-color 0.2s",
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = "#e7f3ff"}
                      onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}>
                      View Details
                    </div>
                    {!hasLateCancelFee && (
                      <div
                        role="menuitem"
                        tabIndex={0}
                        onClick={() => handleDropdownItemClick(handleApplyLateCancelFee, session)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleDropdownItemClick(handleApplyLateCancelFee, session);
                          }
                        }}
                        style={{
                          fontSize: "14px",
                          padding: "8px 16px",
                          color: "#000000",
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#e7f3ff"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}>
                        Apply Late Cancel Fee
                      </div>
                    )}
                  </>
                );
              }

              return (
                <>
                  <div
                    role="menuitem"
                    tabIndex={0}
                    onClick={() => handleDropdownItemClick(handleViewDetails, session)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleDropdownItemClick(handleViewDetails, session);
                      }
                    }}
                    style={{
                      fontSize: "14px",
                      padding: "8px 16px",
                      color: "#000000",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = "#e7f3ff"}
                    onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}>
                    View Details
                  </div>
                  {(() => {
                    const sessionId = session._id || session.id;
                    const sessionDetail = sessionHistoryDetailsBySession[sessionId];
                    const isSigned = sessionDetail?.isSigned === true || sessionDetail?.isSigned === "true";

                    return (
                      <div
                        role="menuitem"
                        tabIndex={0}
                        onClick={() => handleDropdownItemClick(handleCompleteNote, session)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleDropdownItemClick(handleCompleteNote, session);
                          }
                        }}
                        style={{
                          fontSize: "14px",
                          padding: "8px 16px",
                          color: "#000000",
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#e7f3ff"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}>
                        {isSigned ? "View Note" : "Complete Note"}
                      </div>
                    );
                  })()}
                  {(() => {
                    const sessionId = session._id || session.id;
                    const sessionDetail = sessionHistoryDetailsBySession[sessionId];
                    const isSigned = sessionDetail?.isSigned === true || sessionDetail?.isSigned === "true";

                    return (
                      <div
                        role="menuitem"
                        tabIndex={0}
                        onClick={() => {
                          if (isSigned) {
                            handleDropdownItemClick(handleAddAddendum, session);
                          } else {
                            handleDropdownItemClick(handleMarkNoShow, session);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            if (isSigned) {
                              handleDropdownItemClick(handleAddAddendum, session);
                            } else {
                              handleDropdownItemClick(handleMarkNoShow, session);
                            }
                          }
                        }}
                        style={{
                          fontSize: "14px",
                          padding: "8px 16px",
                          color: "#000000",
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#e7f3ff"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}>
                        {isSigned ? "Add Addendum" : "Mark No-Show"}
                      </div>
                    );
                  })()}
                  {(() => {
                    const sessionId = session._id || session.id;
                    const sessionDetail = sessionHistoryDetailsBySession[sessionId];
                    const isSigned = sessionDetail?.isSigned === true || sessionDetail?.isSigned === "true";

                    return (
                      <div
                        role="menuitem"
                        tabIndex={0}
                        onClick={() => {
                          if (isSigned) {
                            handleDownloadNote(session);
                          } else {
                            handleDropdownItemClick(handleMarkCanceled, session);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            if (isSigned) {
                              handleDownloadNote(session);
                            } else {
                              handleDropdownItemClick(handleMarkCanceled, session);
                            }
                          }
                        }}
                        style={{
                          fontSize: "14px",
                          padding: "8px 16px",
                          color: "#000000",
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#e7f3ff"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}>
                        {isSigned ? "Download Note" : "Mark Canceled"}
                      </div>
                    );
                  })()}
                </>
              );
            })()}
          </div>,
          document.body
        );
      })()}

      {/* Mark No-Show Modal */}
      {showNoShowModal && selectedSessionForNoShow && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10002,
            padding: "20px",
          }}
          onClick={closeNoShowModal}>
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              maxWidth: "500px",
              width: "100%",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #e5e5e5",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  backgroundColor: "#FEF3C7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 2L2 22H22L12 2Z"
                    fill="#FBBF24"
                  />
                  <path
                    d="M12 8V12"
                    stroke="#000000"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="12"
                    cy="16"
                    r="1"
                    fill="#000000"
                  />
                </svg>
              </div>
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#000000",
                  margin: 0,
                  fontFamily: "Poppins, sans-serif",
                  lineHeight: "1.2",
                }}>
                Mark Session as No-Show
              </h3>
            </div>

            {/* Session Information */}
            <div
              style={{
                padding: "24px",
              }}>
              {/* Session Info Card */}
              <div
                style={{
                  backgroundColor: "#F9FAFC",
                  borderRadius: "8px",
                  padding: "16px 20px",
                  marginBottom: "20px",
                }}>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#000000",
                    marginBottom: "8px",
                    fontFamily: "Poppins, sans-serif",
                    lineHeight: "1.4",
                  }}>
                  {selectedSessionForNoShow?.health_specialist?.firstname || ""}{" "}
                  {selectedSessionForNoShow?.health_specialist?.lastname || ""}
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#6B7280",
                    fontFamily: "Poppins, sans-serif",
                    lineHeight: "1.4",
                  }}>
                  {(() => {
                    const appointmentTime = selectedSessionForNoShow.appointmenttime || selectedSessionForNoShow.appointmentdate;
                    if (!appointmentTime) return "N/A";

                    const date = new Date(appointmentTime);
                    const endTime = selectedSessionForNoShow.appointmentEndTime ? new Date(selectedSessionForNoShow.appointmentEndTime) : null;

                    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

                    const month = monthNames[date.getMonth()];
                    const day = date.getDate();
                    const year = date.getFullYear();

                    const startTime = date.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    });

                    const endTimeStr = endTime ? endTime.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    }) : "";

                    return `${month} ${day}, ${year}${endTimeStr ? ` • ${startTime} – ${endTimeStr}` : ` • ${startTime}`}`;
                  })()}
                </div>
              </div>

              {/* Prompt Text */}
              <p
                style={{
                  fontSize: "14px",
                  color: "#374151",
                  marginBottom: "24px",
                  lineHeight: "1.5",
                  fontFamily: "Poppins, sans-serif",
                  margin: "0 0 24px 0",
                }}>
                The client didn't attend this session. Would you like to apply or waive the no-show fee?
              </p>

              {/* Action Buttons */}
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "flex-end",
                }}>
                <button
                  onClick={closeNoShowModal}
                  style={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#000000",
                    padding: "8px 16px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    fontFamily: "Poppins, sans-serif",
                    minWidth: "80px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f9fafb";
                    e.target.style.borderColor = "#9ca3af";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#ffffff";
                    e.target.style.borderColor = "#d1d5db";
                  }}>
                  Cancel
                </button>
                <button
                  onClick={handleWaiveFee}
                  style={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#000000",
                    padding: "8px 16px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    fontFamily: "Poppins, sans-serif",
                    minWidth: "100px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f9fafb";
                    e.target.style.borderColor = "#9ca3af";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#ffffff";
                    e.target.style.borderColor = "#d1d5db";
                  }}>
                  Waive Fee
                </button>
                <button
                  onClick={handleApplyFee}
                  style={{
                    backgroundColor: "#1F2937",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#ffffff",
                    padding: "8px 16px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    fontFamily: "Poppins, sans-serif",
                    minWidth: "100px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#111827";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#1F2937";
                  }}>
                  Apply Fee
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mark Canceled Modal */}
      {showCancelModal && selectedSessionForCancel && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10002,
            padding: "20px",
          }}
          onClick={closeCancelModal}>
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              maxWidth: "500px",
              width: "100%",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #e5e5e5",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  backgroundColor: "#FEF3C7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 2L2 22H22L12 2Z"
                    fill="#FBBF24"
                  />
                  <path
                    d="M12 8V12"
                    stroke="#000000"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="12"
                    cy="16"
                    r="1"
                    fill="#000000"
                  />
                </svg>
              </div>
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#000000",
                  margin: 0,
                  fontFamily: "Poppins, sans-serif",
                  lineHeight: "1.2",
                }}>
                Mark Session as Canceled
              </h3>
            </div>

            {/* Session Information */}
            <div
              style={{
                padding: "24px",
              }}>
              {/* Session Info Card */}
              <div
                style={{
                  backgroundColor: "#F9FAFC",
                  borderRadius: "8px",
                  padding: "16px 20px",
                  marginBottom: "20px",
                }}>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "500",
                    color: "#1C293D",
                    marginBottom: "8px",
                    fontFamily: "Poppins, sans-serif",
                    lineHeight: "1.4",
                  }}>
                  {selectedSessionForCancel?.health_specialist?.firstname || ""}{" "}
                  {selectedSessionForCancel?.health_specialist?.lastname || ""}
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#6B7280",
                    fontFamily: "Poppins, sans-serif",
                    lineHeight: "1.4",
                  }}>
                  {(() => {
                    const appointmentTime = selectedSessionForCancel.appointmenttime || selectedSessionForCancel.appointmentdate;
                    if (!appointmentTime) return "N/A";

                    const date = new Date(appointmentTime);
                    const endTime = selectedSessionForCancel.appointmentEndTime ? new Date(selectedSessionForCancel.appointmentEndTime) : null;

                    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

                    const month = monthNames[date.getMonth()];
                    const day = date.getDate();
                    const year = date.getFullYear();

                    const startTime = date.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    });

                    const endTimeStr = endTime ? endTime.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    }) : "";

                    return `${month} ${day}, ${year}${endTimeStr ? ` • ${startTime} – ${endTimeStr}` : ` • ${startTime}`}`;
                  })()}
                </div>
              </div>

              {/* Confirmation Question */}
              <p
                style={{
                  fontSize: "14px",
                  color: "#324158",
                  marginBottom: "24px",
                  lineHeight: "1.5",
                  fontFamily: "Poppins, sans-serif",
                  margin: "0 0 24px 0",
                }}>
                Are you sure you want to mark this session as canceled?
              </p>

              {/* Action Buttons */}
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "flex-end",
                }}>
                <button
                  onClick={closeCancelModal}
                  style={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#46566D",
                    padding: "8px 16px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    fontFamily: "Poppins, sans-serif",
                    minWidth: "120px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f9fafb";
                    e.target.style.borderColor = "#9ca3af";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#ffffff";
                    e.target.style.borderColor = "#d1d5db";
                  }}>
                  No, Go Back
                </button>
                <button
                  onClick={handleConfirmCancel}
                  style={{
                    backgroundColor: "#1F2937",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#ffffff",
                    padding: "8px 16px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    fontFamily: "Poppins, sans-serif",
                    minWidth: "150px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#111827";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#1F2937";
                  }}>
                  Yes, Cancel Session
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionHistory;
